/**
 * Вкладка ЭЭХ объекта ОИ: паспорт (кратко), госконтракты, мероприятия с годами.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Filter, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuthV2";
import { useToast } from "@/hooks/use-toast";
import { eehApi } from "@/services/eeh-api.service";
import { objectCardApi } from "@/services/card-api.service";
import { messageFromThrownApiError } from "@/lib/geometry-api-errors";
import type { Geometry } from "@/types/api-v2";
import type { ObjectCardFieldValue } from "@/types/card-template";
import type {
  EehContract,
  EehContractCreate,
  EehKlsOption,
  EehMeasure,
  EehMeasureCreate,
  EehMeasureYear,
} from "@/types/eeh";

interface EehSectionProps {
  objectId: number;
  geometries: Geometry[];
  onOpenCardTab?: () => void;
}

function klsLabel(options: EehKlsOption[], id?: number | null): string {
  if (id == null) return "—";
  return options.find((o) => o.kls_id === id)?.label ?? `#${id}`;
}

function emptyContract(): EehContractCreate {
  return { number: "", subject: "", notes: "" };
}

function emptyMeasure(): EehMeasureCreate {
  return {
    display_order: 0,
    years: [{ year: new Date().getFullYear(), plan_mln: null, fact_mln: null }],
  };
}

export default function EehSection({ objectId, geometries, onOpenCardTab }: EehSectionProps) {
  const { hasPermission } = useAuth();
  const canView = hasPermission("eeh.view");
  const canManage = hasPermission("eeh.manage");
  const canGenerate = hasPermission("report.generate");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [reportPending, setReportPending] = useState<"pr2" | "pr6" | null>(null);

  const [contractFilter, setContractFilter] = useState<string>("all");
  const [geometryFilter, setGeometryFilter] = useState<string>("all");
  const [currentOnly, setCurrentOnly] = useState(true);

  const [contractDialog, setContractDialog] = useState<EehContract | null | "new">(null);
  const [contractForm, setContractForm] = useState<EehContractCreate>(emptyContract());

  const [measureDialog, setMeasureDialog] = useState<EehMeasure | null | "new">(null);
  const [measureForm, setMeasureForm] = useState<EehMeasureCreate>(emptyMeasure());

  const metaQ = useQuery({
    queryKey: ["eeh-meta", objectId],
    queryFn: () => eehApi.getMeta(objectId),
    enabled: canView,
  });
  const contractsQ = useQuery({
    queryKey: ["eeh-contracts", objectId],
    queryFn: () => eehApi.listContracts(objectId),
    enabled: canView,
  });
  const measuresQ = useQuery({
    queryKey: ["eeh-measures", objectId, contractFilter, geometryFilter, currentOnly],
    queryFn: () =>
      eehApi.listMeasures(objectId, {
        contract_id: contractFilter !== "all" ? Number(contractFilter) : undefined,
        geometry_id: geometryFilter !== "all" ? Number(geometryFilter) : undefined,
        current_only: currentOnly,
      }),
    enabled: canView,
  });
  const cardQ = useQuery({
    queryKey: ["object-card", objectId],
    queryFn: () => objectCardApi.getCard(objectId),
    enabled: canView,
  });

  const meta = metaQ.data;
  const passport = useMemo(() => {
    const values: ObjectCardFieldValue[] = cardQ.data?.object_values ?? [];
    const byKey = (key: string) => {
      const row = values.find((v: ObjectCardFieldValue) => v.field_key === key);
      if (!row) return undefined;
      if (row.display_value) return row.display_value;
      const first = row.values?.[0];
      if (first == null) return undefined;
      return (
        first.value_text ??
        first.value_numeric ??
        (first.kls_id != null ? String(first.kls_id) : undefined) ??
        (first.value_json != null ? JSON.stringify(first.value_json) : undefined)
      );
    };
    return {
      account: byKey("account_number") ?? byKey("obj_account_number"),
      status: byKey("obj_status"),
      regime: byKey("obj_operating_mode"),
    };
  }, [cardQ.data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["eeh-contracts", objectId] });
    void qc.invalidateQueries({ queryKey: ["eeh-measures", objectId] });
  };

  const saveContract = useMutation({
    mutationFn: async () => {
      if (contractDialog === "new") {
        return eehApi.createContract(objectId, contractForm);
      }
      if (contractDialog && typeof contractDialog === "object") {
        return eehApi.updateContract(objectId, contractDialog.id, contractForm);
      }
      throw new Error("Нет контракта");
    },
    onSuccess: () => {
      toast({ title: "Контракт сохранён" });
      setContractDialog(null);
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Ошибка",
        description: messageFromThrownApiError(e),
        variant: "destructive",
      }),
  });

  const deleteContract = useMutation({
    mutationFn: (id: number) => eehApi.deleteContract(objectId, id),
    onSuccess: () => {
      toast({ title: "Контракт удалён" });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Не удалось удалить",
        description: messageFromThrownApiError(e),
        variant: "destructive",
      }),
  });

  const saveMeasure = useMutation({
    mutationFn: async () => {
      const years = (measureForm.years ?? []).map((y) => ({
        year: Number(y.year),
        plan_mln: y.plan_mln === "" || y.plan_mln == null ? null : Number(y.plan_mln),
        fact_mln: y.fact_mln === "" || y.fact_mln == null ? null : Number(y.fact_mln),
        completion_pct:
          y.completion_pct === "" || y.completion_pct == null ? null : Number(y.completion_pct),
        commissioned_mark: y.commissioned_mark ?? null,
        commissioned_at: y.commissioned_at ?? null,
      }));
      if (measureDialog === "new") {
        return eehApi.createMeasure(objectId, { ...measureForm, years });
      }
      if (measureDialog && typeof measureDialog === "object") {
        const { years: _y, ...rest } = measureForm;
        await eehApi.updateMeasure(objectId, measureDialog.id, rest);
        return eehApi.replaceYears(objectId, measureDialog.id, years);
      }
      throw new Error("Нет мероприятия");
    },
    onSuccess: () => {
      toast({ title: "Мероприятие сохранено" });
      setMeasureDialog(null);
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Ошибка",
        description: messageFromThrownApiError(e),
        variant: "destructive",
      }),
  });

  const deleteMeasure = useMutation({
    mutationFn: (id: number) => eehApi.deleteMeasure(objectId, id),
    onSuccess: () => {
      toast({ title: "Мероприятие удалено" });
      invalidate();
    },
    onError: (e) =>
      toast({
        title: "Ошибка",
        description: messageFromThrownApiError(e),
        variant: "destructive",
      }),
  });

  if (!canView) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        Нет права просмотра ЭЭХ (`eeh.view`)
      </div>
    );
  }

  if (metaQ.isLoading || contractsQ.isLoading || measuresQ.isLoading) {
    return (
      <div className="space-y-2 p-1">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const openNewContract = () => {
    setContractForm(emptyContract());
    setContractDialog("new");
  };
  const openEditContract = (c: EehContract) => {
    setContractForm({
      number: c.number,
      subject: c.subject ?? "",
      status_kls_id: c.status_kls_id,
      signed_at: c.signed_at ?? undefined,
      end_at: c.end_at ?? undefined,
      amount_total: c.amount_total != null ? Number(c.amount_total) : null,
      notes: c.notes ?? "",
    });
    setContractDialog(c);
  };
  const openNewMeasure = () => {
    setMeasureForm(emptyMeasure());
    setMeasureDialog("new");
  };
  const openEditMeasure = (m: EehMeasure) => {
    setMeasureForm({
      contract_id: m.contract_id,
      geometry_id: m.geometry_id,
      construct_type_kls_id: m.construct_type_kls_id,
      work_type_kls_id: m.work_type_kls_id,
      status_kls_id: m.status_kls_id,
      work_volume_text: m.work_volume_text,
      grounds_text: m.grounds_text,
      construction_code: m.construction_code,
      period_start_year: m.period_start_year,
      period_end_year: m.period_end_year,
      progress_text: m.progress_text,
      commissioned_at: m.commissioned_at ?? undefined,
      notes: m.notes,
      display_order: m.display_order,
      years: m.years?.length
        ? m.years.map((y) => ({ ...y }))
        : [{ year: new Date().getFullYear() }],
    });
    setMeasureDialog(m);
  };

  const updateYear = (idx: number, patch: Partial<EehMeasureYear>) => {
    const years = [...(measureForm.years ?? [])];
    years[idx] = { ...years[idx], ...patch };
    setMeasureForm({ ...measureForm, years });
  };

  return (
    <div className="space-y-4 text-sm">
      <section className="rounded-md border border-border p-3 space-y-1">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-medium text-foreground">Паспорт ЭЭХ</h3>
          <div className="flex items-center gap-2">
            {canGenerate && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={reportPending != null}
                  onClick={async () => {
                    try {
                      setReportPending("pr2");
                      await eehApi.downloadReport(objectId, "pr2");
                      toast({ title: "PR-2 скачан" });
                    } catch (e) {
                      toast({
                        title: "Ошибка PR-2",
                        description: messageFromThrownApiError(e),
                        variant: "destructive",
                      });
                    } finally {
                      setReportPending(null);
                    }
                  }}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />
                  PR-2
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={reportPending != null}
                  onClick={async () => {
                    try {
                      setReportPending("pr6");
                      await eehApi.downloadReport(objectId, "pr6");
                      toast({ title: "PR-6 скачан" });
                    } catch (e) {
                      toast({
                        title: "Ошибка PR-6",
                        description: messageFromThrownApiError(e),
                        variant: "destructive",
                      });
                    } finally {
                      setReportPending(null);
                    }
                  }}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />
                  PR-6
                </Button>
              </>
            )}
            {onOpenCardTab && (
              <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={onOpenCardTab}>
                Редактировать в карточке
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-1 text-xs text-muted-foreground">
          <div>
            Учётный номер:{" "}
            <span className="text-foreground">{String(passport.account ?? "—")}</span>
          </div>
          <div>
            Статус объекта:{" "}
            <span className="text-foreground">{String(passport.status ?? "—")}</span>
          </div>
          <div>
            Режим эксплуатации:{" "}
            <span className="text-foreground">{String(passport.regime ?? "—")}</span>
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-medium">Госконтракты</h3>
          {canManage && (
            <Button type="button" size="sm" className="h-7" onClick={openNewContract}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Добавить
            </Button>
          )}
        </div>
        {(contractsQ.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">Контракты отсутствуют</p>
        ) : (
          <ul className="space-y-1.5">
            {(contractsQ.data ?? []).map((c) => (
              <li
                key={c.id}
                className="rounded-md border border-border p-2 flex items-start justify-between gap-2"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.number}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.subject || "Без предмета"} ·{" "}
                    {klsLabel(meta?.contract_statuses ?? [], c.status_kls_id)} · мероприятий:{" "}
                    {c.measures_count}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => openEditContract(c)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => deleteContract.mutate(c.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-medium">Мероприятия</h3>
          {canManage && (
            <Button type="button" size="sm" className="h-7" onClick={openNewMeasure}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Добавить
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={contractFilter} onValueChange={setContractFilter}>
            <SelectTrigger className="h-7 w-[140px]">
              <SelectValue placeholder="Контракт" />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все контракты</SelectItem>
              {(contractsQ.data ?? []).map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={geometryFilter} onValueChange={setGeometryFilter}>
            <SelectTrigger className="h-7 w-[140px]">
              <SelectValue placeholder="Геометрия" />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все геометрии</SelectItem>
              {geometries.map((g) => (
                <SelectItem key={g.id} value={String(g.id)}>
                  {g.name || `#${g.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={currentOnly}
              onChange={(e) => setCurrentOnly(e.target.checked)}
            />
            Только текущие
          </label>
        </div>
        {(measuresQ.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">Мероприятия отсутствуют</p>
        ) : (
          <ul className="space-y-1.5">
            {(measuresQ.data ?? []).map((m) => (
              <li
                key={m.id}
                className="rounded-md border border-border p-2 flex items-start justify-between gap-2"
              >
                <div className="min-w-0">
                  <div className="font-medium">
                    {klsLabel(meta?.construct_types ?? [], m.construct_type_kls_id)} ·{" "}
                    {klsLabel(meta?.work_types ?? [], m.work_type_kls_id)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {klsLabel(meta?.measure_statuses ?? [], m.status_kls_id)}
                    {m.geometry_id
                      ? ` · ${geometries.find((g) => g.id === m.geometry_id)?.name ?? `геом. #${m.geometry_id}`}`
                      : ""}
                    {m.contract_id
                      ? ` · ГК ${contractsQ.data?.find((c) => c.id === m.contract_id)?.number ?? m.contract_id}`
                      : ""}
                    {m.years?.length ? ` · лет: ${m.years.map((y) => y.year).join(", ")}` : ""}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => openEditMeasure(m)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => deleteMeasure.mutate(m.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={contractDialog != null} onOpenChange={(o) => !o && setContractDialog(null)}>
        <DialogContent className="max-w-md z-[220]">
          <DialogHeader>
            <DialogTitle>
              {contractDialog === "new" ? "Новый госконтракт" : "Редактировать контракт"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Номер</Label>
              <Input
                value={contractForm.number}
                onChange={(e) => setContractForm({ ...contractForm, number: e.target.value })}
              />
            </div>
            <div>
              <Label>Предмет</Label>
              <Textarea
                value={contractForm.subject ?? ""}
                onChange={(e) => setContractForm({ ...contractForm, subject: e.target.value })}
              />
            </div>
            <div>
              <Label>Статус</Label>
              <Select
                value={contractForm.status_kls_id != null ? String(contractForm.status_kls_id) : ""}
                onValueChange={(v) =>
                  setContractForm({
                    ...contractForm,
                    status_kls_id: v ? Number(v) : null,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {(meta?.contract_statuses ?? []).map((o) => (
                    <SelectItem key={o.kls_id} value={String(o.kls_id)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Примечание</Label>
              <Textarea
                value={contractForm.notes ?? ""}
                onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setContractDialog(null)}>
              Отмена
            </Button>
            <Button
              type="button"
              disabled={!contractForm.number.trim() || saveContract.isPending}
              onClick={() => saveContract.mutate()}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={measureDialog != null} onOpenChange={(o) => !o && setMeasureDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto z-[220]">
          <DialogHeader>
            <DialogTitle>
              {measureDialog === "new" ? "Новое мероприятие" : "Редактировать мероприятие"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Тип работ (группа PR)</Label>
                <Select
                  value={
                    measureForm.construct_type_kls_id != null
                      ? String(measureForm.construct_type_kls_id)
                      : ""
                  }
                  onValueChange={(v) =>
                    setMeasureForm({
                      ...measureForm,
                      construct_type_kls_id: v ? Number(v) : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    {(meta?.construct_types ?? []).map((o) => (
                      <SelectItem key={o.kls_id} value={String(o.kls_id)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Вид работ</Label>
                <Select
                  value={
                    measureForm.work_type_kls_id != null ? String(measureForm.work_type_kls_id) : ""
                  }
                  onValueChange={(v) =>
                    setMeasureForm({
                      ...measureForm,
                      work_type_kls_id: v ? Number(v) : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    {(meta?.work_types ?? []).map((o) => (
                      <SelectItem key={o.kls_id} value={String(o.kls_id)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Статус</Label>
                <Select
                  value={
                    measureForm.status_kls_id != null ? String(measureForm.status_kls_id) : ""
                  }
                  onValueChange={(v) =>
                    setMeasureForm({
                      ...measureForm,
                      status_kls_id: v ? Number(v) : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    {(meta?.measure_statuses ?? []).map((o) => (
                      <SelectItem key={o.kls_id} value={String(o.kls_id)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Контракт</Label>
                <Select
                  value={measureForm.contract_id != null ? String(measureForm.contract_id) : "none"}
                  onValueChange={(v) =>
                    setMeasureForm({
                      ...measureForm,
                      contract_id: v === "none" ? null : Number(v),
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    <SelectItem value="none">Без контракта</SelectItem>
                    {(contractsQ.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Геометрия (здание)</Label>
              <Select
                value={measureForm.geometry_id != null ? String(measureForm.geometry_id) : "none"}
                onValueChange={(v) =>
                  setMeasureForm({
                    ...measureForm,
                    geometry_id: v === "none" ? null : Number(v),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="none">Без привязки</SelectItem>
                  {geometries.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.name || `#${g.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Вид и объём работ</Label>
              <Textarea
                value={measureForm.work_volume_text ?? ""}
                onChange={(e) =>
                  setMeasureForm({ ...measureForm, work_volume_text: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Основания</Label>
              <Textarea
                value={measureForm.grounds_text ?? ""}
                onChange={(e) => setMeasureForm({ ...measureForm, grounds_text: e.target.value })}
              />
            </div>
            <div>
              <Label>Шифр объекта строительства</Label>
              <Input
                value={measureForm.construction_code ?? ""}
                onChange={(e) =>
                  setMeasureForm({ ...measureForm, construction_code: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Капвложения по годам (млн руб.)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7"
                  onClick={() =>
                    setMeasureForm({
                      ...measureForm,
                      years: [
                        ...(measureForm.years ?? []),
                        {
                          year: (measureForm.years?.at(-1)?.year ?? new Date().getFullYear()) + 1,
                        },
                      ],
                    })
                  }
                >
                  + год
                </Button>
              </div>
              {(measureForm.years ?? []).map((y, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-1 items-end">
                  <div>
                    <Label className="text-[10px]">Год</Label>
                    <Input
                      type="number"
                      value={y.year}
                      onChange={(e) => updateYear(idx, { year: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">План</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={y.plan_mln ?? ""}
                      onChange={(e) =>
                        updateYear(idx, {
                          plan_mln: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Факт</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={y.fact_mln ?? ""}
                      onChange={(e) =>
                        updateYear(idx, {
                          fact_mln: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">%</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={y.completion_pct ?? ""}
                      onChange={(e) =>
                        updateYear(idx, {
                          completion_pct: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMeasureDialog(null)}>
              Отмена
            </Button>
            <Button
              type="button"
              disabled={saveMeasure.isPending}
              onClick={() => saveMeasure.mutate()}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
